/**
 * Purchase requisition helpers — the lightweight, cost-first model.
 *
 * An adhoc (non-catalog) line item is outsourced: WSS buys it from a vendor.
 * Before the customer quotation goes out, staff get a quote *from the vendor*
 * and record the per-unit purchase cost + the supplying vendor on the line
 * item itself. There is no separate requisition collection or procurement
 * module — the "requisition" is born with the adhoc line and worked there.
 *
 * These helpers derive the requisition state and the cost-vs-sell margin.
 * They never touch the pricing engine (`pricing.ts`) — purchase cost is
 * internal and is not part of what the customer is charged.
 */

/** The subset of line-item fields the requisition helpers read. `LineItem`
 *  (and `LineItemBase`) are structurally assignable to this. */
export interface RequisitionFields {
  source?: 'catalog' | 'adhoc';
  vendorId?: string;
  vendorName?: string;
  /** Per-unit purchase cost WSS pays the vendor — a string, as typed. */
  purchaseCost?: string;
  /** Per-unit sell price the customer is charged — a string, as typed. */
  adhocUnitPrice?: string;
  qty?: number;
}

/**
 * Requisition state of a line item:
 * - `NONE`    — a catalog item (not outsourced through this flow).
 * - `PENDING` — an adhoc item with no confirmed vendor purchase cost yet.
 * - `QUOTED`  — an adhoc item with a vendor selected AND a positive cost.
 */
export type RequisitionState = 'NONE' | 'PENDING' | 'QUOTED';

export function requisitionState(item: RequisitionFields): RequisitionState {
  if (item.source !== 'adhoc') return 'NONE';
  const cost = parseFloat(item.purchaseCost ?? '');
  if (item.vendorId && cost > 0) return 'QUOTED';
  return 'PENDING';
}

export interface ItemMargin {
  /** Per-unit sell price (>= 0). */
  sell: number;
  /** Per-unit purchase cost (>= 0). */
  cost: number;
  /** Per-unit margin — sell minus cost. May be negative (selling below cost). */
  unitMargin: number;
  /** Margin across the whole line — `unitMargin * qty`. */
  totalMargin: number;
  /** Margin as a fraction of the sell price (0 when sell is 0). */
  marginPct: number;
}

/**
 * Cost-vs-sell margin for a line item. Safe on partially-filled adhoc rows:
 * missing / non-numeric inputs are treated as 0.
 */
export function itemMargin(item: RequisitionFields): ItemMargin {
  const sell = Math.max(0, parseFloat(item.adhocUnitPrice ?? '') || 0);
  const cost = Math.max(0, parseFloat(item.purchaseCost ?? '') || 0);
  const qty = Math.max(0, item.qty ?? 0);
  const unitMargin = sell - cost;
  return {
    sell,
    cost,
    unitMargin,
    totalMargin: unitMargin * qty,
    marginPct: sell > 0 ? unitMargin / sell : 0,
  };
}
